import axios, { CancelTokenSource } from 'axios';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import { AnamnesisSeverityTag } from 'components/patient-entry/AnamnesisSeverityIcon';
import { TreatmentDetails } from 'components/patient-entry/PatientAppointment';
import {
  PatientCentralRefDataConsumerRenderProps,
  withPatientCentralRefDataContext
} from 'components/patient-entry/PatientCentralRefDataContext';
import { PatientEntryConsumerRenderProps, withPatientEntryContext } from 'components/patient-entry/PatientEntryContext';
import QuestionSetView from 'components/patient-entry/QuestionSetView';
import { DataEntry, DataEntryValue, Label } from 'components/patient-entry/styled';
import { renderProblematicPatient } from 'components/patient-entry/upsert/PatientPane';
import moment from 'moment';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouterProps } from 'react-router';
import { Button, Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { DoctorDto, SecondDoseDto, TreatmentBookingDto, UpsertPatientDto } from 'ts-types/api.types';
import { isoToDisplayDate } from 'utils/dateUtils';
import { IndexSignature } from 'utils/praxTypes';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const MainDiv = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  margin: 10px;
`;

const ContentPane = styled.div`
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: min-content minmax(min-content, 1fr);
  column-gap: 3.5rem;
  overflow-y: auto;

  .row-label {
    margin: 1.5rem 0 1rem;
    grid-column-end: span 2;
    justify-self: start;
  }
`;

const DataEntryContainer = styled.div`
  flex: 1 1 auto;
  flex-direction: column;

  .date-time-value {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 1rem;
  }

  .yesno-button.ui.mini.button {
    padding: 0.525rem;
    min-width: 3rem;
    margin-right: 0.5rem;
    font-size: 0.85rem;
  }

`

const ResultsPane = styled.div`
  flex: 0 0 auto;
  margin-right: 15px;
  min-width: 15rem;
  max-width: 36rem;

  .results-header {
    font-weight: bold;
    font-size: 1.1rem;
    border-bottom: 1px solid #e5e5e5;
    margin-bottom: 0.5rem;
  }

  .results-container {
    display: grid;
    grid-template-columns: minmax(min-content, 1fr) minmax(3.5rem, min-content);
    row-gap: 0.35rem;
    column-gap: 1.2rem;

    .result-question {
      color: #575757;
    }

    .result-answer.no-result {
      color: #a5a5a5;
    }
  }

`;

export interface AppointmentDetails {
  place: string;
  date: string;
  time: string;
  onWaitList: boolean;
  reminderDate: string;
  container: string;
  product: string;
}

interface State extends IndexSignature {
  treatmentBooking?: Partial<TreatmentBookingDto>,
  treatmentDetails: TreatmentDetails,
  appointmentDetails: AppointmentDetails,
  componentDidMount: boolean,
  cancelTokenSource: CancelTokenSource
}

interface Props extends MessageSourceProps,
    RouterProps,
    PatientCentralRefDataConsumerRenderProps,
    PatientEntryConsumerRenderProps {

  className?: string;
  patient: UpsertPatientDto;
  treatmentBooking: TreatmentBookingDto;
  secondDoseData?: SecondDoseDto,
  referralDoctor?: DoctorDto
}

class PatientEntrySummaryUI extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    const {
      treatmentBooking,
      containers,
      products,
      getTariffById,
      getReasonById,
      getQuestionSetById,
      getInterventionById,
      getMessage
    } = this.props;

    const tariff = getTariffById(treatmentBooking.tariffId);
    const reason = getReasonById(treatmentBooking.reasonId);
    const questionSet = getQuestionSetById(treatmentBooking.questionSetId);
    const intervention = getInterventionById(treatmentBooking.interventionId);

    const container = containers.find(container => container.id === this.props.treatmentBooking.container);
    const containerDisplay = container ? container.description : "";

    const product = products.find(product => product.id === this.props.treatmentBooking.productId);
    const productDisplay = product ? product.description : "";

    const bookingData = treatmentBooking.bookingData;
    const onWaitList = bookingData.onWaitList;

    const treatmentDetails: TreatmentDetails = {
      date: moment(treatmentBooking.date).format("DD.MM.YYYY"),
      time: treatmentBooking.time,
      tariff: tariff ? tariff.description : "",
      reason: reason ? reason.description : "",
      remarks: treatmentBooking.remarks,
      questionSet: questionSet ? questionSet.name : "",
      anamnesisSeverity: treatmentBooking.anamnesisSeverity ? getMessage(`enum.AnamnesisSeverity.${treatmentBooking.anamnesisSeverity}`) : "",
      intervention: intervention ? `${intervention.description} - ${intervention.durationInMinutes} min` : ""
    }

    const resources = this.props.resources;

    const appointmentDetails: AppointmentDetails = {
      place: resources.find(res => res.id === treatmentBooking.resourceId)!.description,
      date: onWaitList ? "" : isoToDisplayDate(bookingData.date),
      time: bookingData.time,
      onWaitList,
      reminderDate: onWaitList ? isoToDisplayDate(bookingData.date) : "",
      container: containerDisplay,
      product: productDisplay
    }

    this.state = {
      treatmentBooking: this.props.treatmentBooking,
      treatmentDetails,
      appointmentDetails,
      componentDidMount: false,
      cancelTokenSource
    };
  }

  componentDidMount() {
    this.setState({componentDidMount: true});
  }

  componentWillUnmount(): void {
    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  };

  closeTreatmentBooking = () => {
    const {history} = this.props;
    history.push("/patient-central");
  }

  render(): React.ReactNode {

    const {appointmentDetails} = this.state;
    const {patient, questionSets, secondDoseData, treatmentBooking, referralDoctor, getMessage} = this.props;

    const currentQuestionSet = questionSets.find(qs => qs.id === treatmentBooking!.questionSetId);

    let hideTreatmentDetails = true;

    const className = this.props.className;
    return (
        <MainDiv className={className ? className : ""}>
          <ContentPane>
            <DataEntryContainer>

              <DataEntry>
                <Label className="label">{getMessage("anamnesis.form.patient")}:</Label>
                <DataEntryValue>
                  <div>
                    <span style={patient.doctorProblematic ? {color: "red"} : {}}>
                      {`${patient!.firstName} ${patient!.name}`}
                    </span>
                    {renderProblematicPatient(patient)}
                  </div>
                  <div>{`${moment(patient!.birthDate).format("DD.MM.YYYY")}`}</div>
                  <div>{`${patient!.street}`}</div>
                  <div>{`${patient!.zip} ${patient!.city}`}</div>
                </DataEntryValue>

                <div className="label">{getMessage("anamnesis.form.treatment.dateTime")}:</div>
                <div className="value">
                  <div>{`${this.state.treatmentDetails.date} - ${this.state.treatmentDetails.time}`}</div>
                </div>

                {referralDoctor &&
                    <React.Fragment>
                      <div className="label">{getMessage("anamnesis.form.treatment.referralDoctorId")}:</div>
                      <div>
                        <div>
                          {`${referralDoctor.firstName} ${referralDoctor.name}`}&nbsp;
                          {referralDoctor.email ? `(${referralDoctor.email})` : ""}
                        </div>
                      </div>
                    </React.Fragment>
                }

                {
                  !hideTreatmentDetails &&
                  <React.Fragment>
                    <div className="label">{getMessage("anamnesis.form.treatment.tariffId")}:</div>
                    <div className="value">
                      <div>{this.state.treatmentDetails.tariff}</div>
                    </div>

                    <div className="label">{getMessage("anamnesis.form.treatment.reasonId")}:</div>
                    <div className="value">
                      <div>{this.state.treatmentDetails.reason}</div>
                    </div>

                    <div className="label">{getMessage("anamnesis.form.treatment.remarks")}:</div>
                    <div className="value">
                      <div>{this.state.treatmentDetails.remarks}</div>
                    </div>
                  </React.Fragment>
                }

                <div className="label">{getMessage("anamnesis.form.questionSetId")}:</div>
                <div className="value">
                  <div>{this.state.treatmentDetails.questionSet}</div>
                </div>

                <div className="label">{getMessage("anamnesis.form.anamnesisSeverity")}:</div>
                <div className="value">
                  <div>
                    <AnamnesisSeverityTag severity={this.props.treatmentBooking.anamnesisSeverity}/>
                  </div>
                </div>

                <div className="label">{getMessage("anamnesis.form.intervention.interventionId")}:</div>
                <div className="value">
                  <div>{this.state.treatmentDetails.intervention}</div>
                </div>

                <div className="label">{getMessage("anamnesis.form.container")}:</div>
                <div className="value">
                  <div>{appointmentDetails.container}</div>
                </div>

                <div className="label">{getMessage("anamnesis.form.productId")}:</div>
                <div className="value">
                  <div>{appointmentDetails.product}</div>
                </div>

                <div className="label row-label">
                  {
                    secondDoseData
                        ? getMessage("patientCentral.patientEntry.summary.firstDose")
                        : getMessage("patientCentral.patientEntry.summary.appointment")
                  }
                </div>

                <div className="label">{getMessage("patientCentral.patientEntry.appointment.locationId")}:</div>
                <div className="value">
                  <div>{appointmentDetails.place}</div>
                </div>

                {
                  appointmentDetails.onWaitList &&
                  <>
                    <div className="label"></div>
                    <div className="value">
                      <div>
                        <Icon className="status-icon" fitted name="wait"/>
                        {getMessage("patientCentral.patientEntry.appointment.scheduleMode.waitList")}
                      </div>
                    </div>

                    <div className="label">{getMessage("patientCentral.patientEntry.appointment.reminderDate")}:</div>
                    <div className="value">
                      <div>{appointmentDetails.reminderDate}</div>
                    </div>
                  </>
                }

                {
                  !appointmentDetails.onWaitList &&
                  <>
                    <div className="label">{getMessage("patientCentral.patientEntry.appointment.date")}:</div>
                    <div className="value">
                      <div>{appointmentDetails.date}</div>
                    </div>

                    <div className="label">{getMessage("patientCentral.patientEntry.appointment.time")}:</div>
                    <div className="value">
                      <div>{appointmentDetails.time}</div>
                    </div>
                  </>
                }

                {
                  !appointmentDetails.onWaitList && secondDoseData &&
                  <>
                    <div className="label row-label">
                      {getMessage("patientCentral.patientEntry.summary.secondDose")}
                    </div>

                    <div className="label">{getMessage("patientCentral.patientEntry.appointment.locationId")}:</div>
                    <div className="value">
                      <div>{secondDoseData.locationDesc}</div>
                    </div>

                    <div className="label">{getMessage("patientCentral.patientEntry.appointment.date")}:</div>
                    <div className="value">
                      <div>{moment(secondDoseData.date).format("DD.MM.YYYY")}</div>
                    </div>

                    <div className="label">{getMessage("patientCentral.patientEntry.appointment.time")}:</div>
                    <div className="value">
                      <div>{secondDoseData.time}</div>
                    </div>
                  </>
                }
              </DataEntry>
            </DataEntryContainer>

            {
              currentQuestionSet
              && <QuestionSetView
                  questionSetName={currentQuestionSet.name}
                  questionAnswerPairs={
                    treatmentBooking && treatmentBooking.questionAnswerPairs
                        ? treatmentBooking.questionAnswerPairs
                        : []
                  }/>
            }
          </ContentPane>

          <FormButtonsRow>
            <Button type="button"
                    compact
                    color="grey"
                    floated="right"
                    onClick={() => this.closeTreatmentBooking()}
            >
              {getMessage("patientCentral.patientEntry.summary.close")}
            </Button>
          </FormButtonsRow>
        </MainDiv>
    );
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

}

export default withMessages(
    withRouterWorkaround(
        withPatientCentralRefDataContext(
            withPatientEntryContext(PatientEntrySummaryUI))));
